import { getTier, type TierId } from './tiers';

/**
 * The replacement-cost model behind the homepage savings section.
 *
 * SINGLE SOURCE: every headline figure on the page is DERIVED from the rows
 * below — nothing is restated by hand. Change a row and the stack total, the
 * net saving and the first-year figure all move with it.
 *
 * These are market list prices for comparable tiers of each tool, gathered as
 * ranges because every vendor prices by seats, traffic or contacts. They are a
 * like-for-like capability comparison, not a quote, and the page says so.
 */

export type SavingsRow = {
  id: string;
  /** The kitagency module. */
  feature: string;
  /** What a business would otherwise buy, in prose. */
  alternative: string;
  /** Brand keys from brand-icons.ts, plus text-only fallbacks. */
  brands: string[];
  /** Named alternatives with no brand mark available — rendered as initials. */
  otherBrands?: string[];
  /** Monthly market cost range, USD. */
  monthly: [number, number];
  /**
   * Whether this row counts toward the "stitched SaaS stack" monthly total.
   * The site build is excluded because it is agency labour, not a subscription
   * — it is counted separately as upfront capital plus a maintenance retainer.
   */
  inStack: boolean;
  /** One-off build cost range, USD. Only the site build has one. */
  upfront?: [number, number];
  /** Ongoing agency maintenance retainer, USD/month. */
  retainer?: number;
};

export const SAVINGS_ROWS: SavingsRow[] = [
  {
    id: 'build',
    feature: 'Custom site design & engineering',
    alternative: 'Web design agency build on WordPress or Webflow, plus a maintenance retainer',
    brands: ['webflow', 'wordpress'],
    monthly: [300, 300],
    inStack: false,
    upfront: [5_000, 15_000],
    retainer: 300,
  },
  {
    id: 'crm',
    feature: 'Native lead CRM & tracking',
    alternative: 'HubSpot Starter, Salesforce or Pipedrive',
    brands: ['hubspot'],
    otherBrands: ['Salesforce', 'Pipedrive'],
    monthly: [50, 150],
    inStack: true,
  },
  {
    id: 'chatbot',
    feature: '24/7 RAG AI copilot & chatbot',
    alternative: 'Custom LLM API build plus Voiceflow, or Intercom AI',
    brands: ['anthropic', 'intercom'],
    otherBrands: ['Voiceflow'],
    monthly: [99, 499],
    inStack: true,
  },
  {
    id: 'heatmaps',
    feature: 'Visual heatmaps & session recording',
    alternative: 'Hotjar, Microsoft Clarity or CrazyEgg',
    brands: ['hotjar', 'matomo'],
    otherBrands: ['CrazyEgg'],
    monthly: [39, 99],
    inStack: true,
  },
  {
    id: 'abtesting',
    feature: 'Statistical A/B testing engine',
    alternative: 'VWO, Optimizely or Kameleoon',
    brands: [],
    otherBrands: ['VWO', 'Optimizely', 'Kameleoon'],
    monthly: [199, 899],
    inStack: true,
  },
  {
    id: 'scoring',
    feature: 'Automated lead scoring & VIP alerts',
    alternative: 'ActiveCampaign Premier plus Zapier Premium',
    brands: ['zapier'],
    otherBrands: ['ActiveCampaign'],
    monthly: [149, 350],
    inStack: true,
  },
  {
    id: 'cms',
    feature: 'Content editor, blog & CMS',
    alternative: 'Contentful, Sanity.io or managed WordPress hosting',
    brands: ['contentful', 'sanity'],
    monthly: [49, 199],
    inStack: true,
  },
  {
    id: 'email',
    feature: 'Newsletter & transactional email',
    alternative: 'Mailchimp, ConvertKit or Klaviyo',
    brands: ['mailchimp'],
    otherBrands: ['ConvertKit', 'Klaviyo'],
    monthly: [50, 250],
    inStack: true,
  },
  {
    id: 'booking',
    feature: 'Booking & scheduling system',
    alternative: 'Calendly Teams or Acuity Scheduling',
    brands: ['calendly'],
    otherBrands: ['Acuity'],
    monthly: [15, 45],
    inStack: true,
  },
  {
    id: 'commerce',
    feature: 'Multilingual & e-commerce module',
    alternative: 'Shopify Advanced, Weglot or WooCommerce',
    brands: ['shopify', 'woocommerce'],
    otherBrands: ['Weglot'],
    monthly: [99, 399],
    inStack: true,
  },
  /* Scale-only capabilities. Out of the homepage stack total (inStack: false)
     so that figure is unchanged; the Scale pricing card counts them. */
  {
    id: 'personalisation',
    feature: 'Dynamic website personalisation',
    alternative: 'VWO Personalize, Mutiny or Optimizely personalization',
    brands: [],
    otherBrands: ['VWO', 'Mutiny', 'Optimizely'],
    monthly: [199, 999],
    inStack: false,
  },
  {
    id: 'growthlab',
    feature: 'Interactive assessments, calculators and gated guides',
    alternative: 'Outgrow, Typeform or Interact',
    brands: [],
    otherBrands: ['Outgrow', 'Typeform', 'Interact'],
    monthly: [29, 199],
    inStack: false,
  },
  {
    id: 'referral',
    feature: 'Customer referral program with tracked links',
    alternative: 'ReferralCandy, GrowSurf or Rewardful',
    brands: [],
    otherBrands: ['ReferralCandy', 'GrowSurf', 'Rewardful'],
    monthly: [49, 249],
    inStack: false,
  },
];

/** Freelance developer time a business spends on edits it cannot make itself. */
export const DEV_DEPENDENCY = {
  hourlyRate: [75, 150] as [number, number],
  hoursPerMonth: 5,
};

function sumRange(rows: SavingsRow[]): [number, number] {
  return rows.reduce<[number, number]>(
    (acc, r) => [acc[0] + r.monthly[0], acc[1] + r.monthly[1]],
    [0, 0],
  );
}

/** The tier the comparison is anchored to. Growth is the common starting point. */
export const BENCHMARK_TIER: TierId = 'growth';

export function computeSavings(tierId: TierId = BENCHMARK_TIER) {
  const tier = getTier(tierId);
  const tierPrice = tier?.price ?? 0;

  /** Monthly subscriptions a business would stitch together. */
  const stack = sumRange(SAVINGS_ROWS.filter((r) => r.inStack));

  /** One-off agency build. */
  const upfront = SAVINGS_ROWS.reduce<[number, number]>(
    (acc, r) => (r.upfront ? [acc[0] + r.upfront[0], acc[1] + r.upfront[1]] : acc),
    [0, 0],
  );

  /** Agency maintenance retainer. */
  const retainer = SAVINGS_ROWS.reduce((n, r) => n + (r.retainer ?? 0), 0);

  /** Developer dependency for content edits. */
  const devMonthly: [number, number] = [
    DEV_DEPENDENCY.hourlyRate[0] * DEV_DEPENDENCY.hoursPerMonth,
    DEV_DEPENDENCY.hourlyRate[1] * DEV_DEPENDENCY.hoursPerMonth,
  ];

  /** Net monthly software saving after paying for the plan. */
  const netStack: [number, number] = [stack[0] - tierPrice, stack[1] - tierPrice];

  /** Everything recurring that goes away, net of the subscription. */
  const netMonthlyTotal: [number, number] = [
    netStack[0] + retainer + devMonthly[0],
    netStack[1] + retainer + devMonthly[1],
  ];

  /** Upfront capital plus twelve months of recurring saving. */
  const firstYear: [number, number] = [
    upfront[0] + netMonthlyTotal[0] * 12,
    upfront[1] + netMonthlyTotal[1] * 12,
  ];

  return {
    tier,
    tierPrice,
    stack,
    upfront,
    retainer,
    devMonthly,
    netStack,
    netMonthlyTotal,
    firstYear,
  };
}

/**
 * What one plan replaces: the monthly cost of the separate tools and agency
 * retainer it stands in for (low and high ends of the market ranges above),
 * the one-off build it avoids, and the monthly difference against the plan's
 * own price. Used on the pricing cards, so the saving is derived, never typed.
 */
export function tierReplacement(tierId: TierId) {
  const tier = getTier(tierId);
  const rows = SAVINGS_ROWS.filter((r) => tier?.replaces.includes(r.id));
  const monthly = sumRange(rows);
  const upfront = rows.reduce<[number, number]>((acc, r) => (r.upfront ? [acc[0] + r.upfront[0], acc[1] + r.upfront[1]] : acc), [0, 0]);
  const price = tier?.price ?? 0;
  return {
    tools: rows.length,
    monthly,
    upfront,
    /** Conservative: the LOW end of the market range minus the plan price. */
    savedMonthlyFrom: Math.max(0, monthly[0] - price),
    savedFirstYearFrom: Math.max(0, monthly[0] - price) * 12 + upfront[0],
  };
}

/** "$749" / "$2,890" */
export function money(n: number): string {
  return `$${n.toLocaleString('en-US')}`;
}

/** "$749 – $2,890", collapsing to one figure when both ends match. */
export function range([lo, hi]: [number, number]): string {
  return lo === hi ? money(lo) : `${money(lo)} – ${money(hi)}`;
}
